import React, { useState, useEffect } from 'react';
import api from '../../../api';
import './NoticesSection.css';

export default function NoticesSection({ onSeeAllClick }) {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const res = await api.get('/notices.php');
        const allNotices = Array.isArray(res.data) ? res.data : [];
        
        // Sort by date descending so the newest notices show first
        allNotices.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        // Only keep the latest 3 notices for the homepage display
        setNotices(allNotices.slice(0, 3));
      } catch (err) {
        console.error("Failed to fetch notices for landing page:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <section id="notices" className="notices">

      {/* ── Background decorations ── */}
      <div className="notices__bg-blob notices__bg-blob--left" />
      <div className="notices__bg-blob notices__bg-blob--right" />

      <div className="notices__inner">

        {/* ── Header ── */}
        <div className="notices__header">
          <div className="notices__eyebrow">
            <i className="fa-solid fa-bell"></i>
            <span>Student Support</span>
          </div>
          <h2 className="notices__title">
            Latest <span className="notices__title-accent">Notices</span>
          </h2>
          <p className="notices__subtitle">
            Stay updated with the latest announcements, circulars, and important
            information from the library.
          </p>
        </div>

        {/* ── Cards Grid ── */}
        <div className="notices__grid">
          {loading ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
              Loading notices...
            </div>
          ) : notices.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontWeight: 600 }}>
              No active notices at the moment.
            </div>
          ) : (
            notices.map((notice, i) => {
              const categoryClass = (notice.category || '').toLowerCase();
              const categoryIcon = notice.categoryIcon || 'fa-solid fa-bell';
              const tagIcon = notice.tagIcon || 'fa-solid fa-tag';
              const imgUrl = notice.image_url 
                ? (notice.image_url.startsWith('http') || notice.image_url.startsWith('/') 
                  ? notice.image_url 
                  : `${api.defaults.baseURL}/${notice.image_url}`)
                : null;

              return (
                <article
                  className="notice-card"
                  key={notice.id || i}
                  style={{ animationDelay: `${i * 0.12}s` }}
                >
                  {/* Top bar accent */}
                  <div className="notice-card__bar" />

                  {/* Header row */}
                  <div className="notice-card__head">
                    <span className={`notice-card__category notice-card__category--${categoryClass}`}>
                      <i className={categoryIcon}></i>
                      {notice.category}
                    </span>
                    {parseInt(notice.isNew) === 1 && (
                      <span className="notice-card__new">
                        <i className="fa-solid fa-circle" style={{ fontSize: 6 }}></i>
                        New
                      </span>
                    )}
                  </div>

                  {/* Date */}
                  <div className="notice-card__date">
                    <i className="fa-regular fa-calendar"></i>
                    {formatDate(notice.date)}
                  </div>

                  {/* Title */}
                  <h3 className="notice-card__title">{notice.title}</h3>

                  {/* Footer */}
                  <div className="notice-card__footer" style={{ marginTop: 'auto' }}>
                    <span className="notice-card__tag">
                      <i className={tagIcon}></i>
                      {notice.tag || 'Alert'}
                    </span>
                    {imgUrl && (
                      <a href={imgUrl} target="_blank" rel="noopener noreferrer" className="notice-card__link">
                        Read More
                        <i className="fa-solid fa-arrow-right"></i>
                      </a>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </div>

        {/* ── View all button ── */}
        <div className="notices__cta">
          <a 
            href="#notices" 
            className="notices__view-all"
            onClick={(e) => {
              e.preventDefault();
              onSeeAllClick?.();
            }}
          >
            <i className="fa-solid fa-list"></i>
            View All Notices
          </a>
        </div>

      </div>
    </section>
  );
}
